import { Box, type BoxProps } from '@chakra-ui/react'
import { MotionChakraBox } from 'ui'
import { type AihubLayoutConfig, type CartItem, type CartState } from 'ecosystem'
import { UseSearchParamsSuspense } from 'ui/wrappers'
import { AihubProvider } from '../aihubContext'
import type { AihubContextState } from '../types'
import { useAihub } from './useAihub'
import { AihubMain } from './AihubMain'
import { useAihubState } from './useAihubState'
import { AihubTrigger } from './AihubTrigger'
import { resolveAihubVisibility } from '../utils'

interface AIHubProps {
  handleAddToCart: (cartItem: CartItem) => void
  cartState: CartState
  wrapperProps?: BoxProps
  assistant?: AihubContextState['assistant']
  offers?: AihubContextState['offers']
  /** Per-device visibility from `layout.aihub`; omitted/undefined = shown. */
  layoutConfig?: AihubLayoutConfig
}

const AihubComponent = ({
  wrapperProps,
  assistant,
  offers,
  handleAddToCart,
  cartState,
  layoutConfig
}: AIHubProps) => {
  const resolvedVisibility = resolveAihubVisibility(layoutConfig)
  const { showOnDesktop, showOnTablet, showOnMobile, isVisibleAnywhere } = resolvedVisibility
  const { newAiOffers } = useAihub({ offers, visibility: resolvedVisibility })

  const { isOpen, isExpanded } = useAihubState()

  if (!isVisibleAnywhere) {
    return null
  }

  return (
    <AihubProvider
      value={{
        assistant,
        offers,
        handleAddToCart,
        cartState,
        newAiOffers
      }}
    >
      <Box
        className="AIHub"
        // CSS rather than a JS breakpoint check, so the hidden device never flashes the hub
        // on hydration. Tiers: base = mobile, sm = tablet, md = desktop (AihubTrigger edge tab).
        display={{
          base: showOnMobile ? 'block' : 'none',
          sm: showOnTablet ? 'block' : 'none',
          md: showOnDesktop ? 'block' : 'none'
        }}
        zIndex={3}
        position="fixed"
        left={0}
        bottom={0}
        top={{ base: '8vh', md: '14vh' }}
        transition="all 0.25s ease"
        w="100%"
        maxW={{
          base: '100%',
          md: isExpanded ? '65rem' : '25rem'
        }}
        transform="translateX(-100%)"
        {...wrapperProps}
      >
        <AihubTrigger />

        <MotionChakraBox
          initial={{ translateX: '0%' }}
          animate={{ translateX: isOpen ? '100%' : 0 }}
          transition={{ type: 'spring', bounce: 0.4, duration: 0.4 }}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          w="100%"
          h="100%"
          backgroundColor="background.default"
          boxShadow="md"
          borderEndRadius="3xl"
        >
          <AihubMain />
        </MotionChakraBox>
      </Box>
    </AihubProvider>
  )
}

export const Aihub = (props: AIHubProps) => (
  <UseSearchParamsSuspense>
    <AihubComponent {...props} />
  </UseSearchParamsSuspense>
)
